import { parseOptionSymbol, positionToStreamerSymbol } from "@/utils/option-symbol-utils";
import { calculateOptionValues } from "@/utils/option-calculations";
import { getSymbolAliases } from "@/lib/symbols/symbol-identity";
import { resolveQuoteForPosition, type QuoteLike } from "@/utils/quote-resolver";
import type { Position } from "@/types/position.types";
import { symbolMappingService } from "@/services/symbol-mapping-service";

const toNumber = (value: unknown): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const numeric = Number(trimmed);
    return Number.isFinite(numeric) ? numeric : null;
  }
  if (typeof value === "bigint") {
    return Number(value);
  }
  return null;
};

const isPositive = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const extractTimestamp = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) {
      return numeric;
    }
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (value instanceof Date) {
    return Number.isFinite(value.getTime()) ? value.getTime() : null;
  }
  return null;
};

const resolveSymbols = (position: Position) => {
  const baseSymbol = position.symbol?.toString().trim() ?? "";
  const instrumentType = position["instrument-type"] ?? "";

  let streamerSymbol = (position["streamer-symbol"] as string | undefined)?.trim();
  if (!streamerSymbol && instrumentType === "Equity Option") {
    streamerSymbol =
      symbolMappingService.getStreamerSymbol(baseSymbol) ||
      positionToStreamerSymbol(baseSymbol);
  }

  let underlyingSymbol = (position["underlying-symbol"] as string | undefined)?.trim();
  if (!underlyingSymbol && instrumentType === "Equity Option") {
    const parsed = parseOptionSymbol(baseSymbol);
    if (parsed?.underlying) {
      underlyingSymbol = parsed.underlying.trim();
    }
  }

  if (!underlyingSymbol) {
    underlyingSymbol = baseSymbol;
  }

  return {
    baseSymbol,
    streamerSymbol,
    underlyingSymbol,
    isOption: instrumentType?.toLowerCase().includes("option") ?? false,
  };
};

const buildQuote = (
  quoteMap: Map<string, QuoteLike>,
  symbols: Array<string | null | undefined>,
  useCacheFallback: boolean,
): QuoteLike | null => {
  const candidates = new Set<string>();

  const addCandidate = (value: string | null | undefined) => {
    if (!value) {
      return;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return;
    }
    const variations = getSymbolAliases(trimmed);

    variations.forEach((candidate) => {
      if (candidate) {
        candidates.add(candidate);
      }
    });
  };

  symbols.forEach((symbol) => addCandidate(symbol));

  for (const candidate of candidates) {
    const direct = quoteMap.get(candidate);
    if (direct) {
      return direct;
    }
  }

  if (!useCacheFallback) {
    return null;
  }

  for (const candidate of candidates) {
    const cached = resolveQuoteForPosition({
      quoteMap,
      positionSymbol: candidate,
      streamerSymbol: undefined,
      useCacheFallback: true,
      additionalSymbols: [],
    });

    if (cached) {
      return cached;
    }
  }

  return null;
};

const resolveMidpoint = (quote: QuoteLike | null): {
  price: number | null;
  source: PriceSource;
} => {
  if (!quote) {
    return { price: null, source: "none" };
  }

  if (isPositive(quote.midPrice)) {
    return { price: quote.midPrice!, source: "quote-mid" };
  }

  if (isPositive(quote.bidPrice) && isPositive(quote.askPrice)) {
    const mid = (quote.bidPrice! + quote.askPrice!) / 2;
    if (isPositive(mid)) {
      return { price: mid, source: "quote-bid-ask" };
    }
  }

  if (isPositive(quote.lastPrice)) {
    return { price: quote.lastPrice!, source: "quote-last" };
  }

  if (isPositive(quote.askPrice)) {
    return { price: quote.askPrice!, source: "quote-ask" };
  }

  if (isPositive(quote.bidPrice)) {
    return { price: quote.bidPrice!, source: "quote-bid" };
  }

  return { price: null, source: "none" };
};

const DEFAULT_OPTION_MULTIPLIER = 100;

/** Where a position's prior-session day-P&L reference price came from. */
export type DayReferenceSource = "daily-close" | "broker-close" | "none";

/** ------------------------------------------------------------------
 *  Prior-session close reference for day P&L.
 *  ------------------------------------------------------------------
 *  `close-price` is NOT a trustworthy prior-session close. The broker
 *  overwrites it with the position's own `average-open-price` — verified live
 *  2026-08-11 on account 5WX84566: the TSLA 280121C00500000 / C00750000 legs
 *  (opened 2026-06-10) were restamped at 14:09:09Z with
 *  `close-price` == `average-open-price` == 74.3642 / 33.833, and the same
 *  holds for every position opened in the current session (the NFLX condor
 *  opened 13:30:10Z). When that happens `(currentPrice - close-price)`
 *  degenerates to `(currentPrice - entry price)` — i.e. day P&L silently
 *  becomes OPEN P&L, to the cent. That published -$79,406 of "day" loss on a
 *  structure whose real day move was ~+$2.5k.
 *
 *  `average-daily-market-close-price` is the broker's purpose-built
 *  prior-session close and is NOT overwritten: it held 41.4139 / 17.1301 for
 *  those same TSLA legs, which reproduces the correct pre-restamp day P&L to
 *  within rounding, and it matches `close-price` exactly on all 86 of 92
 *  live positions the broker had not restamped. So it is the primary source.
 *
 *  `close-price` remains a LAST resort for payload shapes that omit the daily
 *  field, and only when it is demonstrably not the entry price. If neither
 *  yields a trustworthy reference the answer is null (absence, rendered "-"),
 *  never a number measured from the entry price.
 */
const resolveDayReference = (
  position: Position,
): { price: number | null; source: DayReferenceSource } => {
  const dailyClose = toNumber(
    (position as unknown as Record<string, unknown>)[
      "average-daily-market-close-price"
    ],
  );
  if (isPositive(dailyClose)) {
    return { price: dailyClose, source: "daily-close" };
  }

  const brokerClose = toNumber(position["close-price"]);
  const openPrice = toNumber(position["average-open-price"]);
  if (isPositive(brokerClose) && brokerClose !== openPrice) {
    return { price: brokerClose, source: "broker-close" };
  }

  return { price: null, source: "none" };
};

export type PriceSource =
  | "quote-mid"
  | "quote-bid-ask"
  | "quote-last"
  | "quote-ask"
  | "quote-bid"
  | "close"
  | "market-value"
  | "none";

export interface ContractSnapshot {
  key: string;
  position: Position;
  baseSymbol: string;
  streamerSymbol?: string;
  underlyingSymbol: string;
  instrumentType: string;
  quantity: number;
  multiplier: number;
  strike: number | null;
  tradePrice: number | null;
  currentPrice: number | null;
  priceSource: PriceSource;
  bidPrice: number | null;
  askPrice: number | null;
  lastPrice: number | null;
  quoteTimestamp: number | null;
  underlyingPrice: number | null;
  underlyingTimestamp: number | null;
  contractDelta: number | null;
  positionDelta: number | null;
  gamma: number | null;
  theta: number | null;
  vega: number | null;
  iv: number | null;
  greeksTimestamp: number | null;
  intrinsicValue: number | null;
  extrinsicValue: number | null;
  unrealizedPL: number | null;
  unrealizedPLPercent: number | null;
  dayChange: number | null;
  /** Prior-session close `dayChange` was measured from (null when unknown). */
  dayReference: number | null;
  dayReferenceSource: DayReferenceSource;
  marketValue: number | null;
  costBasis: number | null;
  hasRealtimeData: boolean;
}

export interface UnderlyingSnapshot {
  underlying: string;
  contracts: ContractSnapshot[];
  totalQuantity: number;
  totalMarketValue: number;
  totalDayChange: number;
  totalPL: number;
  totalCost: number;
  totalTheta: number;
  totalPositionDelta: number;
  totalNotional: number;
  underlyingPrice: number | null;
}

export interface SnapshotTotals {
  marketValue: number;
  dayChange: number;
  positionDelta: number;
  notional: number;
}

export interface SnapshotQuoteDetail {
  symbol: string;
  source: PriceSource;
  instrumentType: string;
  price: number | null;
  bid: number | null;
  ask: number | null;
  last: number | null;
  closePrice: number | null;
  dayReference: number | null;
  dayReferenceSource: DayReferenceSource;
  underlyingPrice: number | null;
}

export interface SnapshotDiagnostics {
  /** Symbols where we located a quote and used it as the current price */
  quoteResolved: string[];
  /** Symbols that fell back to the broker close price */
  quoteMissingCloseUsed: string[];
  /** Symbols that still have no usable price after fallbacks */
  quoteMissingNoPrice: string[];
  /** Details for every contract processed */
  entries: SnapshotQuoteDetail[];
}

export interface PositionSnapshotResult {
  contracts: ContractSnapshot[];
  byUnderlying: UnderlyingSnapshot[];
  totals: SnapshotTotals;
  diagnostics?: SnapshotDiagnostics;
}

interface SnapshotInput {
  positions: Position[];
  quotes: Map<string, QuoteLike>;
  greeks: Map<string, Record<string, unknown>>;
  useCacheFallback?: boolean;
  captureDiagnostics?: boolean;
}

class PositionSnapshotService {
  buildSnapshot({
    positions,
    quotes,
    greeks,
    useCacheFallback = true,
    captureDiagnostics = false,
  }: SnapshotInput): PositionSnapshotResult {
    const contracts: ContractSnapshot[] = [];
    const underlyingMap = new Map<string, UnderlyingSnapshot>();

    const diagnostics: SnapshotDiagnostics | undefined = captureDiagnostics
      ? {
          quoteResolved: [],
          quoteMissingCloseUsed: [],
          quoteMissingNoPrice: [],
          entries: [],
        }
      : undefined;

    let totalMarketValue = 0;
    let totalDayChange = 0;
    let totalPositionDelta = 0;
    let totalNotional = 0;

    positions.forEach((position) => {
      const { baseSymbol, streamerSymbol, underlyingSymbol, isOption } = resolveSymbols(position);
      const instrumentType = position["instrument-type"] ?? "";
      const quantity = Number(position.quantity ?? 0);
      const multiplier =
        typeof position.multiplier === "number" && Number.isFinite(position.multiplier)
          ? position.multiplier
          : isOption
            ? DEFAULT_OPTION_MULTIPLIER
            : 1;

      const quote = buildQuote(
        quotes,
        [
          streamerSymbol,
          baseSymbol,
          positionToStreamerSymbol(baseSymbol),
          baseSymbol.replace(/\s+/g, ""),
        ],
        isOption ? useCacheFallback : false,
      );

      const { price: quotePrice, source: initialSource } = resolveMidpoint(quote);
      const closePrice = toNumber(position["close-price"]);
      const marketValueRaw = toNumber(position["market-value"]);
      let currentPrice = quotePrice;
      let priceSource: PriceSource = initialSource;

      if (!isPositive(currentPrice ?? null)) {
        if (isPositive(closePrice)) {
          currentPrice = closePrice ?? null;
          priceSource = "close";
          diagnostics?.quoteMissingCloseUsed.push(baseSymbol);
        } else {
          currentPrice = null;
          priceSource = "none";
          diagnostics?.quoteMissingNoPrice.push(baseSymbol);
        }
      } else {
        diagnostics?.quoteResolved.push(baseSymbol);
      }

      const quoteTimestamp = extractTimestamp((quote as Record<string, unknown> | null)?.timestamp);

      const underlyingQuote = buildQuote(
        quotes,
        [
          underlyingSymbol,
          symbolMappingService.getStreamerSymbol(underlyingSymbol),
          positionToStreamerSymbol(underlyingSymbol),
          underlyingSymbol.replace(/\s+/g, ""),
        ],
        false,
      );

      const { price: resolvedUnderlyingPrice } = resolveMidpoint(underlyingQuote);
      const underlyingTimestamp = extractTimestamp((underlyingQuote as Record<string, unknown> | null)?.timestamp);
      const underlyingPrice = resolvedUnderlyingPrice;

      const greekCandidates = new Set<string>();
      const parsedOption = parseOptionSymbol(baseSymbol);
      const strikePrice = parsedOption?.strike !== undefined ? Number(parsedOption.strike) : null;
      [streamerSymbol, baseSymbol, positionToStreamerSymbol(baseSymbol), baseSymbol.replace(/\s+/g, "")].forEach((candidate) => {
        if (!candidate) {
          return;
        }
        getSymbolAliases(candidate).forEach((alias) => greekCandidates.add(alias));
      });

      let greekEntry: Record<string, unknown> | undefined;
      for (const candidate of greekCandidates) {
        const entry = greeks.get(candidate);
        if (entry) {
          greekEntry = entry;
          break;
        }
      }

      const tradePrice = toNumber(position["average-open-price"]);
      const marketValue = currentPrice !== null ? currentPrice * quantity * multiplier : marketValueRaw;
      const costBasis = tradePrice !== null ? tradePrice * quantity * multiplier : null;

      let unrealizedPL: number | null = null;
      let unrealizedPLPercent: number | null = null;

      if (marketValue !== null && costBasis !== null) {
        unrealizedPL = marketValue - costBasis;
        if (tradePrice && tradePrice !== 0 && currentPrice !== null) {
          const diff = currentPrice - tradePrice;
          const pctRaw = quantity < 0 ? (tradePrice - currentPrice) / tradePrice : diff / tradePrice;
          if (Number.isFinite(pctRaw)) {
            unrealizedPLPercent = pctRaw * 100;
          }
        }
      }

      // Per-position day change. The broker positions payload does not carry a
      // "day-change" field (verified against the live API), so when it is
      // absent derive the real value from data this snapshot already has:
      // (live price - prior close) x qty x multiplier. Only a LIVE quote price
      // qualifies — when the price itself fell back to the close the day move
      // is unknown, and null (not a fake 0) is the honest answer.
      //
      // The prior-close reference comes from resolveDayReference, NOT from
      // `close-price` directly: the broker overwrites `close-price` with the
      // entry price, which silently turns day P&L into open P&L. See that
      // helper's note. A missing/untrustworthy reference yields null.
      const { price: dayReference, source: dayReferenceSource } =
        resolveDayReference(position);
      const brokerDayChange = toNumber(position["day-change"]);
      let dayChange: number | null = brokerDayChange;
      if (
        dayChange === null &&
        currentPrice !== null &&
        dayReference !== null &&
        priceSource !== "close" &&
        priceSource !== "market-value" &&
        priceSource !== "none"
      ) {
        dayChange = (currentPrice - dayReference) * quantity * multiplier;
      }

      const fallbackPositionDelta = toNumber(
        (position as unknown as Record<string, unknown>).delta,
      );
      const greekDelta = toNumber(greekEntry?.delta ?? fallbackPositionDelta) ?? null;
      const contractDelta = isOption
        ? greekDelta
        : quantity === 0
          ? 0
          : 1;
      const positionDelta = contractDelta !== null ? contractDelta * quantity * multiplier : null;

      const gamma = toNumber(greekEntry?.gamma);
      const theta = toNumber(greekEntry?.theta);
      const vega = toNumber(greekEntry?.vega);
      const iv = toNumber(greekEntry?.impliedVolatility);
      const greeksTimestamp = extractTimestamp(greekEntry?.timestamp ?? greekEntry?.updatedAt);

      let intrinsicValue = 0;
      let extrinsicValue = 0;

      if (isOption && currentPrice !== null && underlyingPrice !== null && parsedOption?.optionType && strikePrice !== null) {
        const optionValues = calculateOptionValues(
          parsedOption.optionType,
          strikePrice,
          underlyingPrice,
          currentPrice,
        );
        intrinsicValue = optionValues.intrinsicValue;
        extrinsicValue = optionValues.extrinsicValue;
      }

      const contractSnapshot: ContractSnapshot = {
        key: `${position["account-number"] ?? "UNKNOWN"}:${baseSymbol}`,
        position,
        baseSymbol,
        streamerSymbol,
        underlyingSymbol,
        instrumentType,
        quantity,
        multiplier,
        strike: isOption ? strikePrice : null,
        tradePrice,
        currentPrice,
        priceSource,
        bidPrice: quote?.bidPrice ?? null,
        askPrice: quote?.askPrice ?? null,
        lastPrice: quote?.lastPrice ?? null,
        quoteTimestamp,
        underlyingPrice,
        underlyingTimestamp,
        contractDelta,
        positionDelta,
        gamma,
        theta,
        vega,
        iv,
        greeksTimestamp,
        intrinsicValue,
        extrinsicValue,
        unrealizedPL,
        unrealizedPLPercent,
        dayChange,
        dayReference,
        dayReferenceSource,
        marketValue,
        costBasis,
        hasRealtimeData: Boolean(quote || greekEntry || underlyingQuote),
      };

      diagnostics?.entries.push({
        symbol: baseSymbol,
        source: priceSource,
        instrumentType,
        price: currentPrice,
        bid: quote?.bidPrice ?? null,
        ask: quote?.askPrice ?? null,
        last: quote?.lastPrice ?? null,
        closePrice,
        dayReference,
        dayReferenceSource,
        underlyingPrice,
      });

      contracts.push(contractSnapshot);

      const group = underlyingMap.get(underlyingSymbol) ?? {
        underlying: underlyingSymbol,
        contracts: [] as ContractSnapshot[],
        totalQuantity: 0,
        totalMarketValue: 0,
        totalDayChange: 0,
        totalPL: 0,
        totalCost: 0,
        totalTheta: 0,
        totalPositionDelta: 0,
        totalNotional: 0,
        underlyingPrice: underlyingPrice ?? null,
      };

      group.contracts.push(contractSnapshot);
      group.totalQuantity += quantity;
      group.totalMarketValue += marketValue ?? 0;
      group.totalDayChange += dayChange ?? 0;
      group.totalCost += costBasis ?? 0;
      group.totalPL += unrealizedPL ?? 0;
      group.totalPositionDelta += positionDelta ?? 0;

      const thetaContribution = theta !== null && theta !== undefined ? theta * quantity * multiplier : 0;
      group.totalTheta += Math.abs(thetaContribution);

      if (underlyingPrice !== null && positionDelta !== null) {
        group.totalNotional += positionDelta * underlyingPrice;
      }

      if (group.underlyingPrice === null && underlyingPrice !== null) {
        group.underlyingPrice = underlyingPrice;
      }

      underlyingMap.set(underlyingSymbol, group);

      totalMarketValue += marketValue ?? 0;
      totalDayChange += dayChange ?? 0;
      totalPositionDelta += positionDelta ?? 0;
      if (underlyingPrice !== null && positionDelta !== null) {
        totalNotional += positionDelta * underlyingPrice;
      }
    });

    const byUnderlying = Array.from(underlyingMap.values()).sort(
      (a, b) => Math.abs(b.totalMarketValue) - Math.abs(a.totalMarketValue),
    );

    const totals: SnapshotTotals = {
      marketValue: totalMarketValue,
      dayChange: totalDayChange,
      positionDelta: totalPositionDelta,
      notional: totalNotional,
    };

    const result: PositionSnapshotResult = { contracts, byUnderlying, totals };

    if (diagnostics) {
      result.diagnostics = diagnostics;
    }

    return result;
  }
}

export const positionSnapshotService = new PositionSnapshotService();
